import { memo } from 'react';
import { BadgePlus } from 'lucide-react';
import { useStore } from '@/store/useStore';
import { GramIcon } from './GramIcon';
import { UserTypeIcon } from './UserTypeIcon';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';

interface UserInfoBlockProps {
    onNavigate: (page: string, category?: string) => void;
    compact?: boolean;
}

export const UserInfoBlock = memo(({ onNavigate, compact }: UserInfoBlockProps) => {
    const { user, wallet } = useStore();
    const { impactOccurred } = useHapticFeedback();

    const formatBalance = (num: number) => {
        if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
        if (num >= 1000) return (num / 1000).toFixed(1) + 'k';
        return num.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    };

    if (compact) {
        return (
            <div className="glass w-full rounded-[18px] px-3 py-2 flex items-center gap-3">

                {/* Avatar */}
                <div className="relative shrink-0">
                    <div className="glass-inset w-10 h-10 rounded-xl overflow-hidden">
                        <img
                            src={user?.photo_url || '/icons/default-avatar.svg'}
                            alt="avatar"
                            className="w-full h-full object-cover"
                            onError={(e) => { e.currentTarget.src = '/icons/default-avatar.svg'; }}
                        />
                    </div>
                    <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-md glass-chip flex items-center justify-center">
                        <UserTypeIcon kind="ai" className="w-2.5 h-2.5" />
                    </div>
                </div>

                {/* Name + Balance */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <h3 className="text-xs font-black text-tg-text truncate tracking-tighter leading-tight">
                            {user?.first_name || 'User'}
                        </h3>
                        <span className="text-[9px] text-tg-hint truncate">
                            @{(user?.username || 'user')}
                        </span>
                    </div>
                    <div className="flex items-center gap-1 mt-0.5">
                        <GramIcon className="w-3 h-3" />
                        <span className="text-xs font-black text-tg-text tracking-tighter leading-none">
                            {formatBalance(wallet.available_balance)}
                        </span>
                    </div>
                </div>

                {/* Add Listing Button */}
                <button
                    onClick={() => {
                        impactOccurred('medium');
                        onNavigate('add_username');
                    }}
                    className="shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl bg-gradient-to-t from-[#3390EC]/20 to-[#007AFF]/10 border border-[#3390EC]/40 active:scale-95 transition-all"
                >
                    <BadgePlus className="w-4 h-4 text-[#3390EC]" />
                    <span className="text-[10px] font-black uppercase tracking-tighter text-[#3390EC]">
                        Add Listing
                    </span>
                </button>
            </div>
        );
    }

    return (
        <div className="glass w-full rounded-[24px] p-4 flex items-center gap-4">
            {/* Avatar */}
            <div className="relative shrink-0">
                <div className="glass-inset w-14 h-14 rounded-2xl overflow-hidden">
                    <img
                        src={user?.photo_url || '/icons/default-avatar.svg'}
                        alt="avatar"
                        className="w-full h-full object-cover"
                        onError={(e) => { e.currentTarget.src = '/icons/default-avatar.svg'; }}
                    />
                </div>
                <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-lg glass-chip flex items-center justify-center">
                    <UserTypeIcon kind="ai" className="w-3.5 h-3.5" />
                </div>
            </div>

            {/* Name + Balance */}
            <div className="flex-1 min-w-0">
                <h3 className="text-base font-black text-tg-text truncate tracking-tighter leading-tight">
                    {user?.first_name || 'User'}
                </h3>
                <p className="text-xs text-tg-hint truncate">
                    @{(user?.username || 'user')}
                </p>
                <div className="flex items-center gap-1.5 mt-1.5">
                    <GramIcon className="w-3.5 h-3.5" />
                    <span className="text-sm font-black text-tg-text tracking-tighter leading-none">
                        {formatBalance(wallet.available_balance)}
                    </span>
                </div>
            </div>

            {/* Add Listing Button */}
            <button
                onClick={() => {
                    impactOccurred('medium');
                    onNavigate('add_username');
                }}
                className="shrink-0 flex flex-col items-center gap-0.5 px-3 py-2.5 rounded-2xl bg-gradient-to-t from-[#3390EC]/20 to-[#007AFF]/10 border border-[#3390EC]/40 active:scale-95 transition-all"
            >
                <BadgePlus className="w-5 h-5 text-[#3390EC]" />
                <span className="text-[9px] font-black uppercase tracking-tighter text-[#3390EC]">
                    Add Listing
                </span>
            </button>
        </div>
    );
});

export default UserInfoBlock;